import type { ComponentType, ErrorInfo } from 'react';
import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import matchState from '@/common/utils/match';
import { HomeComponent } from '@/features/build-calculation-context/components/Home/Home';
import { Calculation } from '@/features/execute-calculation-set/components/Calculation/Calculation';
import type { Context } from '@/overmind';
import { useAppState } from '@/overmind';
import type { Route, RouterMachine } from '@/overmind/machines';

import AppLayout from '../AppLayout';
import { Authenticating } from './Authenticating';
import { AuthenticatingArcGis } from './AuthenticatingArcGis';
import { CenteredLoadingPage } from './CenteredLoadingPage';
import { ConfigurationV2 } from './ConfigurationV2';
import { ErrorPage } from './ErrorPage';
import { ExecuteCalculationSet } from './ExecuteCalculationSet';
import { Unauthorized } from './Unauthorized';
import { Text, Flex } from '@volue/wave-react';

function AppContent() {
  const auth = useAppState().auth;
  const router = useAppState().router;
  const tenants = useAppState().tenants;
  const arcgisConfiguration = useAppState().arcgis.config
  return matchState(auth, {
    NOT_INITIALIZED: () => <Authenticating />,
    AUTHENTICATING: () => <Authenticating />,
    AUTHENTICATING_ARCGIS: () => {
      if(arcgisConfiguration.current === "ERROR") {
        return <CenteredErrorMessage errorMessage={<Flex flow="column" gap="spacingS" className="pAl">
            <span>{`Cannot retrieve map configuration (reason: ${arcgisConfiguration.error}).`}</span>
            <span>{`It might be a temporary issue, please try refreshing the page.`}</span>
          </Flex>} />
      }
      if(!isBrowsingConfiguration(router) && tenants.hasAccessToApplication.type === 'HAS_ACCESS') {
        return (
          <Authenticating />
        );
      }

      return (
          getCurrentPageFromRouter(router)
        )
    },
    AWAITING_FEATURE_TOGGLES: () => <CenteredLoadingPage text="Loading..." />,
    FEATURE_TOGGLES_IN_PROGRESS: () => (
      <CenteredLoadingPage text="Loading..." />
    ),
    AWAITING_ARCGIS_AUTHENTICATION: () =>
      !isBrowsingConfiguration(router) &&
      tenants.hasAccessToApplication.type === 'HAS_ACCESS' ? (
        <AuthenticatingArcGis />
      ) : (
        getCurrentPageFromRouter(router)
      ),
    AUTHENTICATED: () => getCurrentPageFromRouter(router),
    ERROR: ({ error }) => <div className="pAl red100">{error}</div>,
    UNAUTHENTICATED: () => null
  });
}

function CenteredErrorMessage(props: {errorMessage: string | React.ReactNode}) {
  return <Flex gap="spacingS" className="pAl jcC h100 w100">
    <Text className="pAl red100 beta txtC" css={{ color: '$colorAccent70' }}>
      <div>{props.errorMessage}</div>
    </Text>
  </Flex>;
}

function isBrowsingConfiguration(router: Context['state']['router']) {
  return (
    router.current === 'VALID_PAGE' &&
    router.currentRoute === 'configurationV2'
  );
}

export default function App() {
  return (
    <AppLayout>
      <ErrorBoundary
        FallbackComponent={props =>
          ErrorPage({ message: 'Something went wrong', fallbackProps: props })
        }
        onError={selectLogger(process.env.NODE_ENV)}
      >
        <AppContent />
      </ErrorBoundary>
    </AppLayout>
  );
}

const pages: Record<Route, ComponentType> = {
  root: HomeComponent,
  rootWithTenantId: HomeComponent,
  executeCalculationSetUsing: ExecuteCalculationSet,
  calculationv2: Calculation,
  signInArcgis: AuthenticatingArcGis,
  unauthorized: Unauthorized,
  configurationV2: ConfigurationV2
};

function selectLogger(environment: string | undefined) {
  if (environment === 'development') {
    return (error: Error, info: ErrorInfo) => {
      console.log(error, info);
    };
  }
  return (() => {}) as (error: Error, info: ErrorInfo) => void;
}

function getCurrentPageFromRouter(router: RouterMachine) {
  return matchState(router, {
    VALID_PAGE: ({ currentRoute }) => {
      const Page = pages[currentRoute];
      return <Page />;
    },
    PAGE_NOT_FOUND: () => (
      <div className="pAl gray75">{'Sorry, page not found'}</div>
    )
  });
}
